<template>
    <div>
        <div v-if="visible">
            <!-- This component isnt used anymore -->
            <!-- Maybe later must be optional? -->
            <web-feedback-location
                v-if="
                    hasAddress &&
                    mode === 'location' &&
                    step === FEEDBACKS_STEPS.CHOOSE_LAYER
                "
                :position="metaData.location.position"
                @close="$_onClose()"
                @next="$_onNext($event)"
            />
            <web-feedback-information
                v-if="
                    mode === 'information' &&
                    step === FEEDBACKS_STEPS.CHOOSE_LAYER
                "
                :webfeedbackInformation="metaData"
                @close="$_onClose()"
                @next="$_onNext($event)"
            />
            <feedback-edit-panel
                v-if="step === FEEDBACKS_STEPS.EDIT"
                :feedbackId="getWebThemeFeedback.id"
                :feedbackObject="feedbackObject"
                :meldingsData="meldingsData"
                :isEditing="false"
                @finished="$_onFinished"
                @cancel="$_onCancel"
            />
        </div>
        <feedback-draw
            v-if="showDrawToolbar && step === FEEDBACKS_STEPS.DRAW"
            :contentTypeId="getWebThemeFeedback.data.feedbackType.contentTypeId"
            :feedbackId="getWebThemeFeedback.id"
            :feedbackType="feedbackType"
            :isEditing="false"
            :feedbackObject="feedbackObject"
            @finished="$_onDrawnPoints($event)"
            @cancel="$_onCancel"
        />
    </div>
</template>

<script>
    import { mapActions, mapState, mapGetters } from 'vuex';
    import OlFeature from 'ol/Feature';
    import OlPoint from 'ol/geom/Point';
    import OlPolygon from 'ol/geom/Polygon';
    import OlLineString from 'ol/geom/LineString';
    import { isSmallDevice } from '../../../../services/utilities/devices';
    import {
        FEEDBACKS_STEPS,
        INTERFACE_PANEL_TRIGGERS,
        FEEDBACKS_GEOMETRY_TYPES
    } from '../../../../constants';
    import Location from './Location.vue';
    import Information from './Information.vue';
    import FeedbackEditPanel from '../feedbacks/FeedbackEdit.vue';
    import FeedbackDraw from '../../feedbacks/FeedbackDraw.vue';

    export default {
        props: { feedbackType: { type: String, default: 'feedback' } },
        components: {
            'web-feedback-location': Location,
            'web-feedback-information': Information,
            'feedback-edit-panel': FeedbackEditPanel,
            'feedback-draw': FeedbackDraw
        },

        data() {
            return {
                step: FEEDBACKS_STEPS.CHOOSE_LAYER,
                type: null,
                feedbackObject: null,
                meldingsData: null,
                FEEDBACKS_STEPS: FEEDBACKS_STEPS
            };
        },

        computed: {
            ...mapState(['uiElements', 'context']),
            ...mapGetters([
                'isMobile',
                'getWebTheme',
                'getWebThemeFeedback',
                'getLayerById',
                'mapIsInSelectableRangeForTerugmeldingen',
                'getClickedInRangeForWebfeedback',
                'getMainMap'
            ]),

            feedbackPanel: function () {
                return this.uiElements[INTERFACE_PANEL_TRIGGERS.WEB_FEEDBACK];
            },
            visible: function () {
                return this.feedbackPanel.visible;
            },
            showDrawToolbar: function () {
                return this.feedbackPanel.showDrawToolbar;
            },
            metaData: function () {
                return this.feedbackPanel.data;
            },
            feedbackLayer() {
                return this.getLayerById(this.getWebThemeFeedback.layerId);
            },
            featuresInLayers: function () {
                if (!this.metaData?.results) {
                    return null;
                }
                return this.metaData.results.map(({ features, layer }) => ({
                    features,
                    layer
                }));
            },
            features: function () {
                if (!this.featuresInLayers) {
                    return null;
                }
                return this.featuresInLayers.flatMap(({ features }) => features);
            },
            mode: function () {
                return this.features?.length > 0 ? 'information' : 'location';
            },
            // Does the feedbacktype contain an adres field?
            hasAddress() {
                return this.feedbackLayer?.extended?.feedbackFields?.some(
                    (field) => field.name === 'adres'
                );
            },
            showToolbar() {
                return this.getWebTheme?.data?.showToolbar;
            },
            showFullScreen() {
                return this.getWebTheme?.data?.showFullScreen;
            },
            showSearch() {
                return this.getWebTheme?.data?.showSearch;
            },
            showGpsTracker() {
                return (
                    this.getMainMap?.gpsTracker &&
                    this.getWebTheme?.data?.showGPSTracker
                );
            },
            // If buttons on the right side are shown, adjust the width of the alert message
            // This is depending on the visibilty of the toolbar/searchbar
            // Because one of the buttons can appear behind the searchbar
            adjustAlertWidth() {
                return this.showToolbar || !this.showSearch
                    ? this.showFullScreen || this.showGpsTracker
                    : this.showFullScreen && this.showGpsTracker;
            }
        },
        watch: {
            mapIsInSelectableRangeForTerugmeldingen(newVal) {
                this.$_displayHelpMessage(false);
            },
            getClickedInRangeForWebfeedback(newVal) {
                if (!newVal) {
                    this.$_displayHelpMessage(true);
                    setTimeout(() => {
                        this.$_displayHelpMessage(false);
                    }, 1500);
                }
                this.$store.commit('SET_CLICKED_IN_RANGE_FOR_WEBFEEDBACK', true);
            },
            visible: {
                handler: function (newVal) {
                    if (this.$store.getters.getWebThemeFeedbackHasPointGeoOnly) {
                        // Calculate the appropriate step
                        this.step = this.$_firstStep();
                        if (newVal && this.step === this.FEEDBACKS_STEPS.EDIT) {
                            // No address field and no existing feedback, go directly to the edit screen
                            this.$_onNext({});
                        }
                    } else {
                        this.step = newVal
                            ? this.$_firstStep()
                            : this.FEEDBACKS_STEPS.DRAW;
                    }
                    this.$_displayHelpMessage(false);
                },
                immediate: true
            }
        },
        methods: {
            ...mapActions(['togglePanel']),
            $_getAlertText() {
                let text = '';
                if (this.mapIsInSelectableRangeForTerugmeldingen) {
                    // Correct zoomlevel for making a webfeedback
                    text = this.$t('feedbackClickMap');
                } else {
                    let searchText = '';
                    // User must zoom in further to make a webfeedback
                    if (this.getWebTheme.data.showSearch) {
                        searchText = isSmallDevice()
                            ? ` ${this.$t('feedbackSearchAddress')}`
                            : ` ${this.$t('feedbackSearchField')}`;
                    }
                    if (isSmallDevice()) {
                        text = searchText
                            ? this.$t('feedbackZoomin', [`${searchText}`])
                            : this.$t('feedbackZoominLocation', ['']);
                    } else {
                        text = this.$t('feedbackZoominLocation', [`${searchText}`]);
                    }
                }
                return text;
            },
            $_displayHelpMessage(blink) {
                if (
                    !this.visible &&
                    this.$store.getters.getWebThemeFeedbackHasPointGeoOnly
                ) {
                    this.$store.commit('SET_ALERT_MESSAGE', {
                        message: this.$_getAlertText(),
                        alertClass: null,
                        variant: 'info',
                        optional: false,
                        hasSearch: this.getWebTheme.data.showSearch,
                        blink,
                        adjustAlertWidth: this.adjustAlertWidth
                    });
                } else {
                    this.$store.commit('SET_ALERT_MESSAGE', {
                        message: null,
                        alertClass: null,
                        variant: null,
                        optional: null,
                        hasSearch: null
                    });
                }
            },
            $_onCancel() {
                this.$_onClose();
            },
            $_onFinished() {
                this.$_onClose();
            },
            getGeometryCenterCoord(geometry) {
                const type = geometry.getType();
                if (type === FEEDBACKS_GEOMETRY_TYPES.POINT) {
                    return geometry.getCoordinates();
                } else if (type === FEEDBACKS_GEOMETRY_TYPES.LINE) {
                    return geometry.getCoordinateAt(0.5);
                } else if (type === FEEDBACKS_GEOMETRY_TYPES.POLYGON) {
                    return geometry.getInteriorPoint().getCoordinates().slice(0, 2);
                } else {
                    return geometry.getCoordinates();
                }
            },
            $_firstStep() {
                // If the feedbacklayer has an address field go to CHOOSE_LAYER
                if (this.hasAddress && this.mode === 'location') {
                    return this.FEEDBACKS_STEPS.EDIT;
                } else {
                    // if an existing feedback was clicked the mode is "information".
                    // Set step to CHOOSE_LAYER so the existing feedback will be displayed.
                    if (this.mode === 'information') {
                        return this.FEEDBACKS_STEPS.CHOOSE_LAYER;
                    } else {
                        // No address field and no existing feedback, go to the edit screen.
                        return this.FEEDBACKS_STEPS.EDIT;
                    }
                }
            },
            async $_onDrawnPoints(feedbackObject) {
                this.feedbackObject = {
                    ...this.feedbackObject,
                    ...feedbackObject,
                    updated: true
                };

                if (!this.feedbackObject.featureObject.feature) {
                    return;
                }

                if (!this.showDrawToolbar) {
                    return;
                }

                const geometry =
                    this.feedbackObject.featureObject.feature.getGeometry();
                const position = this.getGeometryCenterCoord(geometry);
                const data = { location: { position }, results: [] };
                this.$store.commit('TOGGLE_MENU_WEB_FEEDBACK', {
                    active: true,
                    showDrawToolbar: true,
                    data
                });

                this.step = this.$_firstStep();
            },
            $_onClose() {
                this.feedbackObject = null;
                this.meldingsnummer = null;

                this.$store.commit('TOGGLE_MENU_WEB_FEEDBACK', {
                    active: false,
                    showDrawToolbar: false,
                    data: null
                });

                this.$store.commit('SHOW_MENU_FEEDBACK', {
                    active: false,
                    contentTypeId: null
                });

                this.$store.commit('SET_ALERT_MESSAGE', {
                    message: null,
                    alertClass: null,
                    variant: null,
                    optional: null,
                    hasSearch: null
                });

                this.$store.commit('MAP_ADD_MARKER', { point: null });
                this.step = this.FEEDBACKS_STEPS.DRAW;
            },
            $_createFeature(coordinates, geometryType) {
                if (geometryType === FEEDBACKS_GEOMETRY_TYPES.POINT) {
                    return new OlFeature({
                        geometry: new OlPoint(coordinates)
                    });
                }
                if (geometryType === FEEDBACKS_GEOMETRY_TYPES.LINE) {
                    return new OlFeature({
                        geometry: new OlLineString(coordinates)
                    });
                }
                if (geometryType === FEEDBACKS_GEOMETRY_TYPES.POLYGON) {
                    return new OlFeature({
                        geometry: new OlPolygon(coordinates)
                    });
                }
                return undefined;
            },
            $_onNext({
                type,
                data,
                position,
                geometryType = FEEDBACKS_GEOMETRY_TYPES.POINT
            }) {
                // position is filled when an existing feedback is selected
                const savePosition = position || this.metaData.location.position;
                if (
                    position ||
                    this.$store.getters.getWebThemeFeedbackHasPointGeoOnly
                ) {
                    const feature = this.$_createFeature(
                        savePosition,
                        geometryType
                    );
                    this.feedbackObject = {
                        featureObject: {
                            feature,
                            geometryType,
                            location: savePosition.toString()
                        }
                    };
                }

                this.meldingsData =
                    type === 'reportNew' ? { adres: data.adres } : data;
                this.type = type;
                this.step = this.FEEDBACKS_STEPS.EDIT;
            }
        }
    };
</script>

<style lang="scss" scoped>
pre {
    position: fixed;
    top: 20%;
    left: 20%;
    background: white;
}
</style>
